import {
  EventTypeDto,
  ScheduledEventDto,
} from '@act/shared/data-transfer-objects';
import { Button } from '@act/storybook';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import React, { memo } from 'react';
import HtmlDisplayer from 'react-components/components/html-displayer';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import {
  deleteScheduledEventForUser,
  selectScheduledEventById,
  snoozeScheduledEventForUser,
} from 'redux/slices/scheduledEventSlice';
import { userAppSelectors } from 'redux/slices/usersSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import InfoCard, {
  AdditionalFeedCardActions,
} from 'toolkit/info-card/info-card';
import { TaggableChip } from '@act/storybook';
import { AddressFormatter } from 'utilities/formatters/address-formatter';

interface InputProps {
  scheduledEventId: number;
  shouldHighlight?: boolean;
  forwardRef?: React.RefObject<HTMLDivElement>;
}

const ScheduledEventCardComponent = ({
  scheduledEventId,
  shouldHighlight,
  forwardRef,
}: InputProps) => {
  const dispatch = useAppDispatch();
  const event: ScheduledEventDto = useAppSelector(state =>
    selectScheduledEventById(state, scheduledEventId)
  );
  const { createVisit, createMessage, createCall, editScheduledEvent } =
    useInteractionsDialog();

  // If event doesn't exist (e.g., after deletion), don't render the component
  if (!event) {
    return null;
  }

  let chip: React.ReactElement;
  const menuItems: AdditionalFeedCardActions[] = [];

  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const member = membersAppSelectors.useSelectMemberById(event.userId);
  const author = userAppSelectors.useSelectUserById(event.createdBy);

  let subject = 'Reminder';
  let primaryOption: React.ReactElement, secondaryOption: React.ReactElement;
  switch (event.type) {
    case 'call':
      subject = 'Scheduled Call';
      primaryOption = (
        <Button
          variant="text"
          debounced
          onClick={() => createCall({ user: member, scheduledEvent: event })}
        >
          Call Now
        </Button>
      );
      break;
    case 'message':
      subject = 'Scheduled Message';
      primaryOption = (
        <Button
          variant="text"
          onClick={() =>
            createMessage({
              user: member,
              scheduledEventId: event.id,
            })
          }
        >
          Message Now
        </Button>
      );
      break;
    case 'reminder':
      subject = 'Scheduled Reminder';
      primaryOption = (
        <Button
          variant="text"
          debounced
          onClick={() => {
            dispatch(
              deleteScheduledEventForUser({
                params: {
                  userId: event.userId,
                  scheduledEventId: event.id,
                },
              })
            );
          }}
        >
          Clear
        </Button>
      );
      break;
    case 'visit':
      subject = 'Scheduled Visit';
      primaryOption = (
        <Button
          variant="text"
          onClick={() =>
            createVisit({
              userId: member?.id,
              scheduledEvent: event,
            })
          }
        >
          Record Visit
        </Button>
      );
      break;
  }

  const now = DateTime.now();
  let dateTime: DateTime = now;
  let includeTime = false;
  if (event.date) {
    dateTime = DateTime.fromISO(event.date).endOf('day');
  }
  if (event.time) {
    includeTime = true;
    const time = DateTime.fromMillis(event.time);
    dateTime = dateTime.set({
      hour: time.hour,
      minute: time.minute,
      second: time.second,
    });
  }
  const isDue = dateTime < now;
  if (isDue) {
    chip = <TaggableChip isTag color="error" label="Late" />;
    menuItems.push({
      title: 'Snooze',
      details: 'Snooze Reminder',
      handler: () => {
        dispatch(
          snoozeScheduledEventForUser({
            params: {
              userId: event.userId,
              scheduledEventId: event.id,
            },
          })
        );
      },
    });
  }

  if (event.type !== 'reminder') {
    secondaryOption = (
      <Button
        variant="text"
        onClick={() =>
          editScheduledEvent({
            scheduledEvent: event,
            user: member,
          })
        }
      >
        Edit
      </Button>
    );

    menuItems.push({
      title: 'Clear',
      details: 'Clear Reminder',
      handler: () => {
        dispatch(
          deleteScheduledEventForUser({
            params: {
              userId: event.userId,
              scheduledEventId: event.id,
            },
          })
        );
      },
    });
  }

  let location;

  if (event.visitLocation) {
    if (typeof event.visitLocation === 'string') {
      location = event.visitLocation;
    } else {
      location = AddressFormatter.toShort(event.visitLocation);
    }
  }

  const authorName = actingUser?.id === author?.id ? 'you' : author?.name;

  function translateVariant(eventType: EventTypeDto) {
    if (eventType === 'tbd') {
      return 'reminder';
    }
    return eventType;
  }

  return (
    <div ref={forwardRef}>
      <InfoCard
        shouldHighlight={shouldHighlight}
        variant={translateVariant(event.type)}
        subVariant={event.type === 'call' ? 'outgoing' : undefined}
        menuItems={menuItems}
        startTime={dateTime.toMillis()}
        subject={subject}
        includeTime={includeTime}
        primaryOption={primaryOption}
        secondaryOption={secondaryOption}
        statusChips={[chip]}
      >
        <>
          <Typography variant="labelLarge" component="p">
            Created by {authorName}{' '}
          </Typography>
          <Typography variant="bodyMedium" component="p">
            {location}
          </Typography>
          <HtmlDisplayer innerHtml={event.note}></HtmlDisplayer>
        </>
      </InfoCard>
    </div>
  );
};

// Memoize to prevent re-renders when props haven't changed
const ScheduledEventCard = memo(ScheduledEventCardComponent);

export default ScheduledEventCard;
